"use client";

import {
  Container,
  Title,
  Paper,
  Group,
  Button,
  Stack,
  Text,
  Box,
  ColorSwatch,
  Badge,
  Card,
  SimpleGrid,
  Anchor,
  ThemeIcon,
  RingProgress,
} from "@mantine/core";
import { NavigationProgress, setNavigationProgress } from "@mantine/nprogress";
import { IconArrowRight, IconRefresh, IconRobot, IconCheck, IconX, IconUsers, IconClock, IconQuestionMark, IconPalette, IconSparkles, IconEye, IconDroplet, IconSun } from "@tabler/icons-react";
import { useState, useEffect, useRef } from "react";
import { colorSeasons } from "@/constants";
import { Link } from "@/i18n/navigation";
import Image from "next/image";
import TypeformQuiz, { QuizQuestion } from "@/components/TypeformQuiz";
import QuizPhotoUpload from "@/components/QuizPhotoUpload";
import { trackQuizStart, trackQuizComplete } from "@/lib/analytics";
import EmailResultsCapture from "@/components/EmailResultsCapture";
import { useTranslations, useLocale } from "next-intl";
import { determineColorSeason } from "@/lib/determineColorSeason";
import RelatedFinders from "@/components/ShadeFinder/RelatedFinders";

// Questions will be generated using translations in the component

export default function ColorAnalysisQuiz() {
  const locale = useLocale();
  const t = useTranslations("colorAnalysisQuiz");
  const tQuestions = useTranslations("colorAnalysisQuiz.questions");

  const [phase, setPhase] = useState<"intro" | "quiz" | "results">("intro");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showStickyButton, setShowStickyButton] = useState(false);
  const quizStartedTracked = useRef(false);
  const quizCompletedTracked = useRef(false);
  const mainCtaRef = useRef<HTMLButtonElement>(null);

  // Generate questions from translations
  const questions: QuizQuestion[] = [
    {
      id: "undertone",
      title: tQuestions("undertoneTitle"),
      description: tQuestions("undertoneDescription"),
      helpText: tQuestions("undertoneHelp"),
      options: [
        { label: tQuestions("undertoneWarm"), value: "warm" },
        { label: tQuestions("undertoneCool"), value: "cool" },
        { label: tQuestions("undertoneNeutral"), value: "neutral" },
        { label: tQuestions("undertoneNotSure"), value: "not_sure" },
      ],
    },
    {
      id: "hair",
      title: tQuestions("hairTitle"),
      description: tQuestions("hairDescription"),
      helpText: tQuestions("hairHelp"),
      options: [
        { label: tQuestions("hairDark"), value: "dark", color: "#1a1a1a" },
        { label: tQuestions("hairMediumBrown"), value: "medium_brown", color: "#6B4423" },
        { label: tQuestions("hairWarmBlonde"), value: "warm_blonde", color: "#D4A574" },
        { label: tQuestions("hairCoolBlonde"), value: "cool_blonde", color: "#A9A9A9" },
        { label: tQuestions("hairRed"), value: "red", color: "#A52A2A" },
      ],
    },
    {
      id: "eyes",
      title: tQuestions("eyesTitle"),
      description: tQuestions("eyesDescription"),
      helpText: tQuestions("eyesHelp"),
      options: [
        { label: tQuestions("eyesDarkBrown"), value: "dark_brown", color: "#3B1F1F" },
        { label: tQuestions("eyesLightBrown"), value: "light_brown", color: "#8B7355" },
        { label: tQuestions("eyesGreen"), value: "green", color: "#3CB371" },
        { label: tQuestions("eyesBlue"), value: "blue", color: "#4169E1" },
      ],
    },
    {
      id: "contrast",
      title: tQuestions("contrastTitle"),
      description: tQuestions("contrastDescription"),
      helpText: tQuestions("contrastHelp"),
      options: [
        { label: tQuestions("contrastHigh"), value: "high" },
        { label: tQuestions("contrastMedium"), value: "medium" },
        { label: tQuestions("contrastLow"), value: "low" },
      ],
    },
    {
      id: "tanning",
      title: tQuestions("tanningTitle"),
      description: tQuestions("tanningDescription"),
      options: [
        { label: tQuestions("tanningBurns"), value: "burns" },
        { label: tQuestions("tanningBurnsTans"), value: "burns_tans" },
        { label: tQuestions("tanningTans"), value: "tans" },
        { label: tQuestions("tanningAlways"), value: "always_tans" },
      ],
    },
  ];

  // Reset progress on mount
  useEffect(() => {
    setNavigationProgress(0);
  }, []);

  // Show sticky button only after scrolling past the main CTA
  useEffect(() => {
    if (phase !== "intro") return;

    const handleScroll = () => {
      if (mainCtaRef.current) {
        const rect = mainCtaRef.current.getBoundingClientRect();
        setShowStickyButton(rect.bottom < 0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [phase]);

  const startQuiz = () => {
    if (!quizStartedTracked.current) {
      trackQuizStart("Color Analysis Quiz");
      quizStartedTracked.current = true;
    }
    setPhase("quiz");
  };

  const handleQuizComplete = (quizAnswers: Record<string, string>) => {
    setAnswers(quizAnswers);
    setPhase("results");
  };

  const handleExit = () => {
    setPhase("intro");
    setNavigationProgress(0);
  };

  const resetQuiz = () => {
    setPhase("intro");
    setAnswers({});
    setNavigationProgress(0);
    quizStartedTracked.current = false;
    quizCompletedTracked.current = false;
  };

  if (phase === "results") {
    const { season, subSeason } = determineColorSeason(answers);

    // Track quiz completion
    if (!quizCompletedTracked.current) {
      trackQuizComplete("Color Analysis Quiz");
      quizCompletedTracked.current = true;
    }

    const seasonData = colorSeasons[season]?.subseasons[subSeason];

    if (!seasonData) {
      return (
        <Container size="xl" py="xl">
          <NavigationProgress size={4} color="teal" />
          <Title ta="center" mb="xl">
            {t("results.errorTitle")}
          </Title>
          <Paper shadow="sm" p={{ base: "xs", md: "lg" }} radius="md" mb="xl">
            <Stack gap="md">
              <Text>
                {t("results.errorMessage")}
              </Text>
              <Button variant="default" onClick={resetQuiz} leftSection={<IconRefresh size="1rem" />}>
                {t("results.startOver")}
              </Button>
              <Text fw="bold">
                {t("results.recommendAI")}
              </Text>
              <Button
                variant="filled"
                component={Link}
                href="/ai-color-analysis"
                color="violet"
                fullWidth
                leftSection={<IconRobot size={20} />}
              >
                {t("results.getCompleteAnalysis")}
              </Button>
            </Stack>
          </Paper>
        </Container>
      );
    }

    return (
      <Container size="xl" py="xl">
        <NavigationProgress size={4} color="teal" />

        <Stack gap="lg" align="center">
          <Card withBorder p="xl" radius="md" w="100%">
            <Stack gap="lg" ta="center" align="center">
              <Badge size="lg" color="teal" variant="light">
                {t("results.yourResult")}
              </Badge>
              <Text fz={48}>{seasonData.emoji}</Text>
              <Title order={2} c="teal.7">
                {subSeason} {season}
              </Title>

              <Group justify="center" gap="xs">
                {seasonData.primaryColors.slice(0, 6).map((color, i) => (
                  <ColorSwatch key={i} color={color.hex} size={32} />
                ))}
              </Group>

              <Text size="sm" c="dimmed">
                {t("results.basedOnAnswers", { subSeason, season })}
              </Text>

              {/* Optional photo analysis and report preview follow the free result. */}
              <Text size="xs" c="dimmed">
                {t("results.quizAccuracy")} {t("results.accuracyNote")}
              </Text>

              {locale === "en" && <Paper withBorder p="md" radius="md" w="100%">
                <Text fw={600} mb="xs">What colors look best on me in practice?</Text>
                <Text size="sm" mb="sm">Your quiz result is a starting point. Compare these colors against your own face before changing your wardrobe.</Text>
                <Button component={Link} href="/virtual-color-draping" variant="light" color="teal">Try my colors with free draping</Button>
              </Paper>}
              <QuizPhotoUpload quizName="Color Analysis Quiz" resultLabel={`${subSeason} ${season}`} sampleReportSource="color_analysis_quiz_result" />

              <Button
                variant="subtle"
                color="gray"
                component={Link}
                href={`/color-palette/${season.toLowerCase()}/${subSeason.toLowerCase()}`}
                size="sm"
              >
                {t("results.viewPalette", { subSeason, season })}
              </Button>

              <Box w="100%">
                <EmailResultsCapture
                  source="color-analysis-quiz"
                  season={season}
                  subSeason={subSeason}
                  resultLabel={`${subSeason} ${season}`}
                  resultUrl={`/color-palette/${season.toLowerCase()}/${subSeason.toLowerCase()}`}
                />
              </Box>
            </Stack>
          </Card>

          <RelatedFinders source="/color-analysis-quiz" compact />
          <Button variant="subtle" onClick={resetQuiz} leftSection={<IconRefresh size="1rem" />} color="gray" size="sm">
            {t("results.takeAgain")}
          </Button>
        </Stack>
      </Container>
    );
  }

  if (phase === "quiz") {
    return (
      <TypeformQuiz
        questions={questions}
        onComplete={handleQuizComplete}
        onExit={handleExit}
        accentColor="#0D9488" // teal
      />
    );
  }

  // Intro phase
  return (
    <>
      <Container size="xl" py="xl">
        <NavigationProgress size={4} color="teal" />

        {/* ============================================ */}
        {/* HERO SECTION WITH IMAGE */}
        {/* ============================================ */}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" mb="xl">
          {/* Left: Content */}
          <Stack gap="md" justify="center">
            <Title order={1} size="h1" lh={1.2}>
              {t("hero.title")}
            </Title>
            <Title order={2} size="h3" fw={400} c="dimmed">
              {t("hero.subtitle")}
            </Title>

            {locale === "en" && <Group gap="md" mt="xs">
              <Badge variant="light" color="teal">5 questions</Badge>
              <Badge variant="light" color="teal">Free result</Badge>
              <Badge variant="light" color="teal">No photo needed</Badge>
            </Group>}

            <Button
              ref={mainCtaRef}
              size="xl"
              onClick={startQuiz}
              rightSection={<IconArrowRight size="1.2rem" />}
              color="teal"
              mt="sm"
            >
              {t("hero.takeFreeQuiz")}
            </Button>

            <Group gap="lg">
              <Group gap={4}>
                <IconClock size={14} color="gray" />
                <Text size="xs" c="dimmed">{t("hero.minutes")}</Text>
              </Group>
              <Group gap={4}>
                <IconQuestionMark size={14} color="gray" />
                <Text size="xs" c="dimmed">{t("hero.questions")}</Text>
              </Group>
              <Text size="xs" c="dimmed">{t("hero.noSignup")}</Text>
            </Group>
          </Stack>

          {/* Right: Hero Image */}
          <Box
            style={{
              position: "relative",
              width: "100%",
              aspectRatio: "4/3",
              borderRadius: 16,
              overflow: "hidden",
            }}
          >
            <Image
              src="/images/quiz/hero-color-swatches.png"
              alt={t("hero.imageAlt")}
              fill
              style={{ objectFit: "cover" }}
              priority
            />
          </Box>
        </SimpleGrid>

        {/* ============================================ */}
        {/* INTERACTIVE QUESTION PREVIEW */}
        {/* ============================================ */}
        <Card withBorder radius="md" p="lg" mb="xl" style={{ cursor: "pointer" }} onClick={startQuiz}>
          <Group justify="space-between" mb="md">
            <Badge size="sm" color="teal" variant="light">{t("preview.question1of5")}</Badge>
            <Text size="xs" c="dimmed">{t("hero.noSignup")}</Text>
          </Group>
          <Text fw={600} size="lg" mb="sm">{t("preview.undertoneQuestion")}</Text>
          <Text size="sm" c="dimmed" mb="md">
            {t("preview.undertoneHelp")}
          </Text>
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
            {[
              { label: t("preview.warm"), sublabel: t("preview.warmSub") },
              { label: t("preview.cool"), sublabel: t("preview.coolSub") },
              { label: t("preview.neutral"), sublabel: t("preview.neutralSub") },
              { label: t("preview.notSure"), sublabel: t("preview.notSureSub") },
            ].map((opt) => (
              <Card key={opt.label} withBorder padding="sm" radius="md" style={{ textAlign: "center" }}>
                <Text size="sm" fw={500}>{opt.label}</Text>
                <Text size="xs" c="dimmed">{opt.sublabel}</Text>
              </Card>
            ))}
          </SimpleGrid>
          <Group justify="center" mt="md">
            <Button size="sm" color="teal" variant="light" rightSection={<IconArrowRight size={14} />}>
              {t("preview.startQuiz")}
            </Button>
          </Group>
        </Card>

        {/* ============================================ */}
        {/* HOW THE QUIZ WORKS - VISUAL STEPS */}
        {/* ============================================ */}
        <Box mb="xl">
          <Title order={2} size="h3" ta="center" mb="xs">{t("howItWorks.title")}</Title>
          <Text ta="center" c="dimmed" mb="lg">{t("howItWorks.subtitle")}</Text>

          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
            <Card withBorder padding="lg" radius="md" ta="center">
              <ThemeIcon size={48} color="teal" variant="light" radius="xl" mx="auto" mb="md">
                <IconQuestionMark size={24} />
              </ThemeIcon>
              <Text fw={600} mb="xs">{t("howItWorks.step1Title")}</Text>
              <Text size="sm" c="dimmed">
                {t("howItWorks.step1Desc")}
              </Text>
            </Card>
            <Card withBorder padding="lg" radius="md" ta="center">
              <ThemeIcon size={48} color="violet" variant="light" radius="xl" mx="auto" mb="md">
                <IconSparkles size={24} />
              </ThemeIcon>
              <Text fw={600} mb="xs">{t("howItWorks.step2Title")}</Text>
              <Text size="sm" c="dimmed">
                {t("howItWorks.step2Desc")}
              </Text>
            </Card>
            <Card withBorder padding="lg" radius="md" ta="center">
              <ThemeIcon size={48} color="pink" variant="light" radius="xl" mx="auto" mb="md">
                <IconPalette size={24} />
              </ThemeIcon>
              <Text fw={600} mb="xs">{t("howItWorks.step3Title")}</Text>
              <Text size="sm" c="dimmed">
                {t("howItWorks.step3Desc")}
              </Text>
            </Card>
          </SimpleGrid>
        </Box>

        {/* ============================================ */}
        {/* WHAT THE QUIZ ASKS - QUICK OVERVIEW */}
        {/* ============================================ */}
        {locale === "en" && <Paper withBorder p="lg" radius="md" mb="xl">
          <Title order={2} size="h3" mb="sm">What color looks best on me?</Title>
          <Text mb="sm">Look for colors that let you notice your face before the fabric. Compare warm and cool shades, then lighter and deeper colors, then soft and bright versions. Your undertone, natural depth and contrast help narrow the starting palette.</Text>
          <Text size="sm">The quiz suggests a season from your answers. For a visual comparison, <Link href="/virtual-color-draping">try the free draping tool on your photo</Link>. For the differences between systems, <Link href="/color-seasons-chart#16-season-color-analysis">compare the 12- and 16-season charts</Link>.</Text>
        </Paper>}
        <Box mb="xl" py="lg" px="md" style={{ background: "var(--mantine-color-gray-0)", borderRadius: 12 }}>
          <Title order={3} size="h4" mb="md">{t("evaluates.title")}</Title>
          <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="md">
            <Group gap="xs">
              <ThemeIcon size="sm" color="teal" variant="light"><IconDroplet size={14} /></ThemeIcon>
              <Text size="sm">{t("evaluates.undertone")}</Text>
            </Group>
            <Group gap="xs">
              <ThemeIcon size="sm" color="orange" variant="light"><IconSun size={14} /></ThemeIcon>
              <Text size="sm">{t("evaluates.hairColor")}</Text>
            </Group>
            <Group gap="xs">
              <ThemeIcon size="sm" color="blue" variant="light"><IconEye size={14} /></ThemeIcon>
              <Text size="sm">{t("evaluates.eyeColor")}</Text>
            </Group>
            <Group gap="xs">
              <ThemeIcon size="sm" color="violet" variant="light"><IconSparkles size={14} /></ThemeIcon>
              <Text size="sm">{t("evaluates.contrast")}</Text>
            </Group>
            <Group gap="xs">
              <ThemeIcon size="sm" color="yellow" variant="light"><IconSun size={14} /></ThemeIcon>
              <Text size="sm">{t("evaluates.sunReaction")}</Text>
            </Group>
          </SimpleGrid>
        </Box>

        {/* ============================================ */}
        {/* QUIZ-SPECIFIC FAQ - MOVED HIGHER */}
        {/* ============================================ */}
        <Box mb="xl">
          <Title order={2} size="h3" mb="lg">{t("faq.title")}</Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Card withBorder padding="md" radius="md">
              <Text fw={600} mb="xs">{t("faq.accuracy")}</Text>
              <Text size="sm" c="dimmed">
                {t("faq.accuracyAnswer")}
              </Text>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Text fw={600} mb="xs">{t("faq.whatSeason")}</Text>
              <Text size="sm" c="dimmed">
                {t("faq.whatSeasonAnswer")}
              </Text>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Text fw={600} mb="xs">{t("faq.lighting")}</Text>
              <Text size="sm" c="dimmed">
                {t("faq.lightingAnswer")}
              </Text>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Text fw={600} mb="xs">{t("faq.isFree")}</Text>
              <Text size="sm" c="dimmed">
                {t("faq.isFreeAnswer")}
              </Text>
            </Card>
          </SimpleGrid>
        </Box>

        {/* Brief intro - condensed */}
        <Stack gap="md" mt="xl">
          <Text>
            {t("intro.paragraph1")}
          </Text>
          <Box>
            <Text mb="xs">{t("intro.benefitsTitle")}</Text>
            <Stack gap="xs" ml="md">
              <Text>• {t("intro.benefit1")}</Text>
              <Text>• {t("intro.benefit2")}</Text>
              <Text>• {t("intro.benefit3")}</Text>
              <Text>• {t("intro.benefit4")}</Text>
            </Stack>
          </Box>
        </Stack>

        {/* ============================================ */}
        {/* BELOW THE FOLD - SEO CONTENT */}
        {/* ============================================ */}

        {/* The 4 Seasons Visual */}
        <Box mt={60}>
          <Title order={2} size="h3" mb="md">{t("fourSeasons.title")}</Title>
          <Box
            style={{
              position: "relative",
              width: "100%",
              aspectRatio: "1/1",
              maxWidth: 500,
              margin: "0 auto",
              borderRadius: 12,
              overflow: "hidden",
            }}
            mb="lg"
          >
            <Image
              src="/images/quiz/four-seasons-color-wheel.png"
              alt={t("fourSeasons.imageAlt")}
              fill
              style={{ objectFit: "cover" }}
            />
          </Box>
          <Text c="dimmed" mb="md">
            {t("fourSeasons.description")}
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
            <Box>
              <Text fw={600} mb={4}>{t("fourSeasons.springTitle")}</Text>
              <Text size="sm" c="dimmed">{t("fourSeasons.springDesc")}</Text>
            </Box>
            <Box>
              <Text fw={600} mb={4}>{t("fourSeasons.summerTitle")}</Text>
              <Text size="sm" c="dimmed">{t("fourSeasons.summerDesc")}</Text>
            </Box>
            <Box>
              <Text fw={600} mb={4}>{t("fourSeasons.autumnTitle")}</Text>
              <Text size="sm" c="dimmed">{t("fourSeasons.autumnDesc")}</Text>
            </Box>
            <Box>
              <Text fw={600} mb={4}>{t("fourSeasons.winterTitle")}</Text>
              <Text size="sm" c="dimmed">{t("fourSeasons.winterDesc")}</Text>
            </Box>
          </SimpleGrid>
        </Box>

        {/* Inline CTA */}
        <Box ta="center" py={40}>
          <Button
            size="lg"
            onClick={startQuiz}
            rightSection={<IconArrowRight size="1rem" />}
            color="teal"
            variant="light"
          >
            {t("fourSeasons.findYourSeason")}
          </Button>
        </Box>

        {/* Related Tools Section */}
        <Box mt={40} py="lg" px="md" style={{ background: "var(--mantine-color-gray-0)", borderRadius: 12 }}>
          <Title order={2} size="h4" mb="md">Related Color Analysis Tools</Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
            <Anchor component={Link} href="/skin-tone-test" underline="never">
              <Card withBorder padding="md" radius="md" className="hover-lift">
                <Text fw={600} size="sm">Skin Tone Test</Text>
                <Text size="xs" c="dimmed">Learn about your undertone and find which colors complement your skin</Text>
              </Card>
            </Anchor>
            <Anchor component={Link} href="/style-personality-quiz" underline="never">
              <Card withBorder padding="md" radius="md" className="hover-lift">
                <Text fw={600} size="sm">Style Personality Quiz</Text>
                <Text size="xs" c="dimmed">Discover your fashion archetype: Classic, Romantic, Dramatic, or more</Text>
              </Card>
            </Anchor>
            <Anchor component={Link} href="/best-colors-for-skin-tone" underline="never">
              <Card withBorder padding="md" radius="md" className="hover-lift">
                <Text fw={600} size="sm">Best Colors for Your Skin Tone</Text>
                <Text size="xs" c="dimmed">See which colors look best on warm, cool, and neutral undertones</Text>
              </Card>
            </Anchor>
            <Anchor component={Link} href="/kibbe-body-type-test" underline="never">
              <Card withBorder padding="md" radius="md" className="hover-lift">
                <Text fw={600} size="sm">Kibbe Body Type Test</Text>
                <Text size="xs" c="dimmed">Find your Kibbe body type to match your colors with the right silhouettes</Text>
              </Card>
            </Anchor>
          </SimpleGrid>
        </Box>

        {/* How This Quiz Works */}
        <Box mt={20}>
          <Title order={2} size="h3" mb="md">How Does This Color Palette Quiz Work?</Title>
          <Text c="dimmed" mb="lg">
            This DIY color analysis uses the same principles as professional color consultants to help you find your color palette. Here's what we evaluate:
          </Text>

          <Stack gap="lg">
            <Box>
              <Text fw={600} mb={4}>1. Skin Undertone</Text>
              <Text size="sm" c="dimmed">
                The most important factor. Your undertone is the subtle color beneath your skin's surface—either warm (yellow/golden), cool (pink/blue), or neutral. Unlike your skin tone (how light or dark you are), your undertone never changes, even when you tan.
              </Text>
              {locale === "en" && (
                <Text size="sm" mt="xs">
                  Unsure about this step? Take the <Anchor component={Link} href="/undertone-quiz">free undertone quiz</Anchor>
                  {" "}for a warm, cool, or neutral starting point and help when the tests disagree.
                </Text>
              )}
            </Box>

            <Box>
              <Text fw={600} mb={4}>2. Natural Hair Color</Text>
              <Text size="sm" c="dimmed">
                Your natural hair color (not dyed) contains either warm pigments (gold, copper, red) or cool pigments (ash, blue-black). This helps narrow down your season. We ask about your hair at age 20 if it's grayed since.
              </Text>
            </Box>

            <Box>
              <Text fw={600} mb={4}>3. Eye Color</Text>
              <Text size="sm" c="dimmed">
                Eyes contribute to your overall coloring. Warm-toned eyes (brown, hazel, amber) lean toward Spring/Autumn. Cool-toned eyes (blue, gray, cool green) lean toward Summer/Winter.
              </Text>
            </Box>

            <Box>
              <Text fw={600} mb={4}>4. Contrast Level</Text>
              <Text size="sm" c="dimmed">
                The difference between your lightest and darkest features. High contrast (dark hair + light skin) suggests Winter or Bright Spring. Low contrast (all medium tones) suggests Summer or Soft Autumn.
              </Text>
            </Box>

            <Box>
              <Text fw={600} mb={4}>5. Sun Reaction</Text>
              <Text size="sm" c="dimmed">
                How your skin responds to sun exposure provides additional clues about your underlying pigmentation and helps confirm your undertone assessment.
              </Text>
            </Box>
          </Stack>
        </Box>

        {/* Vein Test Section with Image */}
        <Box mt={60}>
          <Title order={2} size="h3" mb="md">The Vein Test: How to Tell If You're Warm or Cool</Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" mb="lg">
            <Box
              style={{
                position: "relative",
                width: "100%",
                aspectRatio: "4/3",
                borderRadius: 12,
                overflow: "hidden",
              }}
            >
              <Image
                src="/images/quiz/vein-test-wrist.png"
                alt="Wrist vein test for determining skin undertone"
                fill
                style={{ objectFit: "cover" }}
              />
            </Box>
            <Box>
              <Text c="dimmed" mb="md">
                One of the most reliable ways to determine your undertone is the vein test. In natural daylight, look at the veins on the inside of your wrist:
              </Text>
              <Stack gap="sm">
                <Text size="sm"><strong>Blue or purple veins</strong> → Cool undertone (Summer or Winter)</Text>
                <Text size="sm"><strong>Green or olive veins</strong> → Warm undertone (Spring or Autumn)</Text>
                <Text size="sm"><strong>Both blue and green</strong> → Neutral undertone (could be any season)</Text>
              </Stack>
              <Text size="sm" c="dimmed" mt="md" fs="italic">
                Tip: Do this test near a window with natural light—artificial lighting can distort the colors.
              </Text>
            </Box>
          </SimpleGrid>
        </Box>

        {/* The 12 Subseasons */}
        <Box mt={60}>
          <Title order={2} size="h3" mb="md">The 12-Season Color Analysis System</Title>
          <Text c="dimmed" mb="lg">
            While the basic system has 4 seasons, professional color analysis typically uses 12 subseasons for more precision. Each season has 3 variations based on whether your coloring leans more warm, cool, bright, or muted.
          </Text>

          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="md">
            {/* Spring */}
            <Anchor component={Link} href="/color-palette/spring/light" underline="never">
              <Card padding="sm" radius="md" withBorder style={{ cursor: "pointer" }} className="hover-lift">
                <Text size="sm" fw={600}>Light Spring</Text>
                <Group gap={4} mt={4}>
                  {["#FFEFD5", "#FFB6C1", "#98FB98"].map((c) => (
                    <ColorSwatch key={c} color={c} size={16} />
                  ))}
                </Group>
              </Card>
            </Anchor>
            <Anchor component={Link} href="/color-palette/spring/true" underline="never">
              <Card padding="sm" radius="md" withBorder style={{ cursor: "pointer" }} className="hover-lift">
                <Text size="sm" fw={600}>True Spring</Text>
                <Group gap={4} mt={4}>
                  {["#FF7F50", "#FFD700", "#32CD32"].map((c) => (
                    <ColorSwatch key={c} color={c} size={16} />
                  ))}
                </Group>
              </Card>
            </Anchor>
            <Anchor component={Link} href="/color-palette/spring/bright" underline="never">
              <Card padding="sm" radius="md" withBorder style={{ cursor: "pointer" }} className="hover-lift">
                <Text size="sm" fw={600}>Bright Spring</Text>
                <Group gap={4} mt={4}>
                  {["#FF6347", "#00CED1", "#FF1493"].map((c) => (
                    <ColorSwatch key={c} color={c} size={16} />
                  ))}
                </Group>
              </Card>
            </Anchor>

            {/* Summer */}
            <Anchor component={Link} href="/color-palette/summer/light" underline="never">
              <Card padding="sm" radius="md" withBorder style={{ cursor: "pointer" }} className="hover-lift">
                <Text size="sm" fw={600}>Light Summer</Text>
                <Group gap={4} mt={4}>
                  {["#E6E6FA", "#B0C4DE", "#DDA0DD"].map((c) => (
                    <ColorSwatch key={c} color={c} size={16} />
                  ))}
                </Group>
              </Card>
            </Anchor>
            <Anchor component={Link} href="/color-palette/summer/true" underline="never">
              <Card padding="sm" radius="md" withBorder style={{ cursor: "pointer" }} className="hover-lift">
                <Text size="sm" fw={600}>True Summer</Text>
                <Group gap={4} mt={4}>
                  {["#778899", "#BC8F8F", "#6B8E9F"].map((c) => (
                    <ColorSwatch key={c} color={c} size={16} />
                  ))}
                </Group>
              </Card>
            </Anchor>
            <Anchor component={Link} href="/color-palette/summer/cool" underline="never">
              <Card padding="sm" radius="md" withBorder style={{ cursor: "pointer" }} className="hover-lift">
                <Text size="sm" fw={600}>Cool Summer</Text>
                <Group gap={4} mt={4}>
                  {["#4682B4", "#9370DB", "#708090"].map((c) => (
                    <ColorSwatch key={c} color={c} size={16} />
                  ))}
                </Group>
              </Card>
            </Anchor>

            {/* Autumn */}
            <Anchor component={Link} href="/color-palette/autumn/soft" underline="never">
              <Card padding="sm" radius="md" withBorder style={{ cursor: "pointer" }} className="hover-lift">
                <Text size="sm" fw={600}>Soft Autumn</Text>
                <Group gap={4} mt={4}>
                  {["#D2B48C", "#8FBC8F", "#CD853F"].map((c) => (
                    <ColorSwatch key={c} color={c} size={16} />
                  ))}
                </Group>
              </Card>
            </Anchor>
            <Anchor component={Link} href="/color-palette/autumn/true" underline="never">
              <Card padding="sm" radius="md" withBorder style={{ cursor: "pointer" }} className="hover-lift">
                <Text size="sm" fw={600}>True Autumn</Text>
                <Group gap={4} mt={4}>
                  {["#B8860B", "#CD5C5C", "#556B2F"].map((c) => (
                    <ColorSwatch key={c} color={c} size={16} />
                  ))}
                </Group>
              </Card>
            </Anchor>
            <Anchor component={Link} href="/color-palette/autumn/deep" underline="never">
              <Card padding="sm" radius="md" withBorder style={{ cursor: "pointer" }} className="hover-lift">
                <Text size="sm" fw={600}>Deep Autumn</Text>
                <Group gap={4} mt={4}>
                  {["#8B4513", "#800000", "#2F4F4F"].map((c) => (
                    <ColorSwatch key={c} color={c} size={16} />
                  ))}
                </Group>
              </Card>
            </Anchor>

            {/* Winter */}
            <Anchor component={Link} href="/color-palette/winter/cool" underline="never">
              <Card padding="sm" radius="md" withBorder style={{ cursor: "pointer" }} className="hover-lift">
                <Text size="sm" fw={600}>Cool Winter</Text>
                <Group gap={4} mt={4}>
                  {["#4169E1", "#C71585", "#008B8B"].map((c) => (
                    <ColorSwatch key={c} color={c} size={16} />
                  ))}
                </Group>
              </Card>
            </Anchor>
            <Anchor component={Link} href="/color-palette/winter/true" underline="never">
              <Card padding="sm" radius="md" withBorder style={{ cursor: "pointer" }} className="hover-lift">
                <Text size="sm" fw={600}>True Winter</Text>
                <Group gap={4} mt={4}>
                  {["#FF0000", "#0000CD", "#FFFFFF"].map((c) => (
                    <ColorSwatch key={c} color={c} size={16} />
                  ))}
                </Group>
              </Card>
            </Anchor>
            <Anchor component={Link} href="/color-palette/winter/deep" underline="never">
              <Card padding="sm" radius="md" withBorder style={{ cursor: "pointer" }} className="hover-lift">
                <Text size="sm" fw={600}>Deep Winter</Text>
                <Group gap={4} mt={4}>
                  {["#191970", "#8B0000", "#006400"].map((c) => (
                    <ColorSwatch key={c} color={c} size={16} />
                  ))}
                </Group>
              </Card>
            </Anchor>
          </SimpleGrid>
        </Box>

        {/* Another CTA */}
        <Box ta="center" py={40}>
          <Button
            size="lg"
            onClick={startQuiz}
            rightSection={<IconArrowRight size="1rem" />}
            color="teal"
            variant="light"
          >
            Take the Quiz Now
          </Button>
        </Box>

        {/* How to Know What Colors Look Good on You */}
        <Box mt={20}>
          <Title order={2} size="h3" mb="md">How to Know What Colors Look Good on You</Title>
          <Text c="dimmed" mb="lg">
            Beyond taking a quiz, here are practical ways to figure out what colors look best on you:
          </Text>
          <Stack gap="lg">
            <Box>
              <Text fw={600} mb={4}>1. The White vs Cream Test</Text>
              <Text size="sm" c="dimmed">
                Hold pure white fabric next to your face, then off-white/cream. If white makes you look vibrant, you're likely cool-toned. If cream looks better, you're probably warm-toned.
              </Text>
            </Box>
            <Box>
              <Text fw={600} mb={4}>2. The Jewelry Test</Text>
              <Text size="sm" c="dimmed">
                Do you look better in silver or gold jewelry? Silver flatters cool undertones (Summer/Winter), while gold complements warm undertones (Spring/Autumn).
              </Text>
            </Box>
            <Box>
              <Text fw={600} mb={4}>3. The Clothing Experiment</Text>
              <Text size="sm" c="dimmed">
                Notice which colors get you compliments. When people say "you look great today," pay attention to what you're wearing. Your best colors naturally attract positive feedback.
              </Text>
            </Box>
            <Box>
              <Text fw={600} mb={4}>4. The Contrast Check</Text>
              <Text size="sm" c="dimmed">
                Look at old photos where you felt you looked your best. High contrast people (dark hair + light skin) often look best in bold, saturated colors. Low contrast people thrive in softer, muted tones.
              </Text>
            </Box>
          </Stack>
        </Box>

        {/* How to Determine Your Color Season */}
        <Box mt={60}>
          <Title order={2} size="h3" mb="md">How to Determine Your Color Season</Title>
          <Text c="dimmed" mb="lg">
            Figuring out what season you are comes down to two key questions:
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg" mb="lg">
            <Card withBorder padding="md" radius="md">
              <Text fw={600} mb="sm">Question 1: Warm or Cool?</Text>
              <Text size="sm" c="dimmed" mb="sm">
                This determines whether you're Spring/Autumn (warm) or Summer/Winter (cool). Use the vein test, jewelry test, or notice whether you tan golden or burn pink.
              </Text>
              <Text size="sm">
                <strong>Warm:</strong> Spring or Autumn<br/>
                <strong>Cool:</strong> Summer or Winter
              </Text>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Text fw={600} mb="sm">Question 2: Bright or Muted?</Text>
              <Text size="sm" c="dimmed" mb="sm">
                This separates the seasons within each temperature. High contrast features suggest clearer seasons, while low contrast suggests softer ones.
              </Text>
              <Text size="sm">
                <strong>Warm + Bright:</strong> Spring<br/>
                <strong>Warm + Muted:</strong> Autumn<br/>
                <strong>Cool + Bright:</strong> Winter<br/>
                <strong>Cool + Muted:</strong> Summer
              </Text>
            </Card>
          </SimpleGrid>
          <Text c="dimmed" size="sm">
            Still unsure? Many people fall between seasons—that's where the 12-season system helps. For example, if you're warm but not sure if you're Spring or Autumn, you might be a <Anchor component={Link} href="/color-palette/autumn/soft" c="teal">Soft Autumn</Anchor>, which bridges both.
          </Text>
        </Box>

        {/* DIY Color Analysis at Home */}
        <Box mt={60}>
          <Title order={2} size="h3" mb="md">DIY Color Analysis at Home</Title>
          <Text c="dimmed" mb="lg">
            Want to do your own color analysis at home? Here's a step-by-step approach:
          </Text>
          <Stack gap="md">
            <Box>
              <Text fw={600} mb={4}>Step 1: Set Up Proper Lighting</Text>
              <Text size="sm" c="dimmed">
                Natural daylight is essential—artificial lighting distorts colors. Stand near a window without direct sunlight. Avoid wearing makeup and pull your hair back.
              </Text>
            </Box>
            <Box>
              <Text fw={600} mb={4}>Step 2: Gather Test Fabrics</Text>
              <Text size="sm" c="dimmed">
                Collect solid-colored scarves, shirts, or fabric in various colors. You'll need warm colors (orange, coral, golden yellow) and cool colors (pink, magenta, blue-red).
              </Text>
            </Box>
            <Box>
              <Text fw={600} mb={4}>Step 3: Drape and Compare</Text>
              <Text size="sm" c="dimmed">
                Hold each fabric under your chin and observe your face. The right colors make your skin look even, your eyes bright, and your features defined. Wrong colors create shadows, highlight imperfections, or make you look tired.
              </Text>
            </Box>
            <Box>
              <Text fw={600} mb={4}>Step 4: Take Photos</Text>
              <Text size="sm" c="dimmed">
                Photograph yourself with different color drapes. Looking at photos often reveals color effects you miss in the mirror. Compare side by side.
              </Text>
            </Box>
          </Stack>
          <Text size="sm" c="dimmed" mt="lg" fs="italic">
            Pro tip: For more accurate results without the guesswork, try our <Anchor component={Link} href="/ai-color-analysis" c="teal">AI color analysis</Anchor> which does the draping comparison digitally.
          </Text>
        </Box>

        {/* Common Season Confusions */}
        <Box mt={60}>
          <Title order={2} size="h3" mb="md">Soft Summer vs Soft Autumn: How to Tell the Difference</Title>
          <Text c="dimmed" mb="lg">
            These two seasons are commonly confused because both are soft and muted. Here's how to tell them apart:
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg" mb="lg">
            <Card withBorder padding="md" radius="md">
              <Group gap="xs" mb="sm">
                {["#6B8E9F", "#9370DB", "#708090", "#BC8F8F"].map((c) => (
                  <ColorSwatch key={c} color={c} size={20} />
                ))}
              </Group>
              <Text fw={600} mb="sm">Soft Summer</Text>
              <Text size="sm" c="dimmed">
                Cool undertone with muted coloring. Looks best in dusty blues, lavender, soft rose, and cool grays. Skin has pink or neutral undertones. Often has ash-toned hair.
              </Text>
              <Anchor component={Link} href="/color-palette/summer/cool" c="teal" size="sm" mt="xs">
                View Soft Summer palette →
              </Anchor>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Group gap="xs" mb="sm">
                {["#D2B48C", "#8FBC8F", "#CD853F", "#A0522D"].map((c) => (
                  <ColorSwatch key={c} color={c} size={20} />
                ))}
              </Group>
              <Text fw={600} mb="sm">Soft Autumn</Text>
              <Text size="sm" c="dimmed">
                Warm undertone with muted coloring. Looks best in olive, camel, terracotta, and warm taupes. Skin has golden or peachy undertones. Often has golden-brown or auburn hair.
              </Text>
              <Anchor component={Link} href="/color-palette/autumn/soft" c="teal" size="sm" mt="xs">
                View Soft Autumn palette →
              </Anchor>
            </Card>
          </SimpleGrid>
          <Text c="dimmed" size="sm">
            <strong>The key difference:</strong> Soft Summers look best in colors with a cool, blue-based undertone. Soft Autumns look best in colors with a warm, yellow-based undertone. If you can't tell, look at whether silver or gold jewelry flatters you more.
          </Text>
        </Box>

        {/* Common Mistakes to Avoid */}
        <Box mt={60}>
          <Title order={2} size="h3" mb="md">Common Color Analysis Mistakes to Avoid</Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Text fw={600} mb={4}>Mistake #1: Confusing Tan with Undertone</Text>
              <Text size="sm" c="dimmed">
                A tan doesn't change your undertone. Look at areas that don't tan (like your inner wrist) or consider your natural coloring before any sun exposure.
              </Text>
            </Box>
            <Box>
              <Text fw={600} mb={4}>Mistake #2: Relying Only on Hair Color</Text>
              <Text size="sm" c="dimmed">
                Hair color can be changed, but your skin undertone is constant. Focus on your natural skin undertone rather than your current hair color.
              </Text>
            </Box>
            <Box>
              <Text fw={600} mb={4}>Mistake #3: Ignoring Contrast Level</Text>
              <Text size="sm" c="dimmed">
                Contrast between your features is just as important as undertone. High contrast suggests Winter or Spring, while low contrast suggests Summer or Autumn.
              </Text>
            </Box>
            <Box>
              <Text fw={600} mb={4}>Mistake #4: Testing in Bad Lighting</Text>
              <Text size="sm" c="dimmed">
                Artificial lighting distorts colors. Always do color tests near a window with natural light, and remove makeup for accurate results.
              </Text>
            </Box>
          </SimpleGrid>
        </Box>

        {/* Quiz Accuracy Section */}
        <Box mt={60}>
          <Title order={2} size="h3" mb="md">How Accurate Is This Free Color Analysis?</Title>
          <Text c="dimmed" mb="md">
            Let's be honest: no online color analysis or color palette test can match a professional in-person consultation. Here's why:
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg" mb="lg">
            <Card withBorder padding="md" radius="md">
              <Text fw={600} mb="sm" c="teal">What This Quiz Can Do</Text>
              <Stack gap="xs">
                <Group gap="xs"><IconCheck size={16} color="teal" /><Text size="sm">Suggest a likely season from your answers</Text></Group>
                <Group gap="xs"><IconCheck size={16} color="teal" /><Text size="sm">Point you in the right direction</Text></Group>
                <Group gap="xs"><IconCheck size={16} color="teal" /><Text size="sm">Help you understand the color analysis system</Text></Group>
                <Group gap="xs"><IconCheck size={16} color="teal" /><Text size="sm">Give you colors to experiment with</Text></Group>
              </Stack>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Text fw={600} mb="sm" c="red">What This Quiz Can't Do</Text>
              <Stack gap="xs">
                <Group gap="xs"><IconX size={16} color="red" /><Text size="sm">See your actual skin undertone</Text></Group>
                <Group gap="xs"><IconX size={16} color="red" /><Text size="sm">Account for subtle color variations</Text></Group>
                <Group gap="xs"><IconX size={16} color="red" /><Text size="sm">Identify your exact subseason with certainty</Text></Group>
                <Group gap="xs"><IconX size={16} color="red" /><Text size="sm">Replace professional color draping</Text></Group>
              </Stack>
            </Card>
          </SimpleGrid>
          <Text c="dimmed" size="sm">
            For photo-based guidance, try our <Anchor component={Link} href="/ai-color-analysis" c="teal">AI-powered color analysis</Anchor> which uses your photo to suggest a seasonal palette. Lighting and photo quality affect the result.
          </Text>
        </Box>

        {/* Color Draping Section */}
        <Box mt={60}>
          <Title order={2} size="h3" mb="md">Why Colors Matter: The Draping Effect</Title>
          <Box
            style={{
              position: "relative",
              width: "100%",
              aspectRatio: "16/9",
              borderRadius: 12,
              overflow: "hidden",
            }}
            mb="lg"
          >
            <Image
              src="/images/quiz/color-draping-comparison.png"
              alt="Color draping comparison showing how different colors affect skin appearance"
              fill
              style={{ objectFit: "cover" }}
            />
          </Box>
          <Text c="dimmed" mb="md">
            When you hold the right colors near your face, something magical happens. The right colors act like a natural filter—they reflect light onto your face in a way that:
          </Text>
          <Stack gap="xs" mb="lg">
            <Text size="sm">• Makes your skin look smoother and more even</Text>
            <Text size="sm">• Reduces the appearance of dark circles</Text>
            <Text size="sm">• Makes your eyes appear brighter and more vibrant</Text>
            <Text size="sm">• Creates a healthy, natural-looking glow</Text>
          </Stack>
          <Text c="dimmed">
            The wrong colors do the opposite—they can make you look tired, washed out, or even sickly. This is why color analysis matters: it's not about wearing "pretty" colors, it's about wearing colors that make <em>you</em> look pretty.
          </Text>
        </Box>

        {/* FAQ Section */}
        <Box mt={60}>
          <Title order={2} size="h3" mb="lg">Frequently Asked Questions</Title>
          <Stack gap="lg">
            <Box>
              <Text fw={600} mb={4}>Can my color season change over time?</Text>
              <Text size="sm" c="dimmed">
                Your basic undertone doesn't change, but your best subseason might shift slightly as you age. Hair graying, skin changes, and reduced contrast can move you toward a softer or lighter version of your season. However, a Spring will always be a Spring—you won't suddenly become a Winter.
              </Text>
            </Box>
            <Box>
              <Text fw={600} mb={4}>What if I'm between two seasons?</Text>
              <Text size="sm" c="dimmed">
                Many people fall on the cusp between seasons—these are often the "neutral" subtypes like Soft Autumn (between Summer and Autumn) or Bright Spring (between Spring and Winter). If you're borderline, you may be able to wear colors from both palettes.
              </Text>
            </Box>
            <Box>
              <Text fw={600} mb={4}>Does ethnicity affect my color season?</Text>
              <Text size="sm" c="dimmed">
                Color seasons exist across all ethnicities. People of any background can be any season—it depends on the undertones and characteristics of your individual coloring, not your ethnic background. There are Black Winters and Asian Springs and everything in between.
              </Text>
            </Box>
            <Box>
              <Text fw={600} mb={4}>Should I use my dyed hair color or natural hair?</Text>
              <Text size="sm" c="dimmed">
                For the most accurate result, think about your natural hair color (or the color it was before it grayed). Your natural coloring is what determines your season. However, if you always dye your hair a certain color, you might choose clothing colors that work with your current look.
              </Text>
            </Box>
            <Box>
              <Text fw={600} mb={4}>Why do some color analysis systems have different names?</Text>
              <Text size="sm" c="dimmed">
                You might see terms like "Soft Summer" vs "Light Summer" or "Deep Winter" vs "Dark Winter." Different color analysts use slightly different naming conventions, but they generally refer to the same concepts. Our quiz uses the most common 12-season terminology.
              </Text>
            </Box>
            <Box>
              <Text fw={600} mb={4}>What's the difference between this quiz and AI color analysis?</Text>
              <Text size="sm" c="dimmed">
                This quiz relies on your self-assessment of your features. Our <Anchor component={Link} href="/ai-color-analysis" c="teal">AI color analysis</Anchor> uses a photo to assess your coloring and suggest a palette. Neither approach guarantees a definitive season; compare the recommendations in natural light.
              </Text>
            </Box>
          </Stack>
        </Box>

        {/* Final CTA */}
        <Box ta="center" py={60}>
          <Title order={2} size="h3" mb="md">Ready to Find Your Color Palette?</Title>
          <Text c="dimmed" mb="lg" maw={400} mx="auto">
            Take this free color analysis test to discover what colors look best on you and start wearing shades that make you look amazing.
          </Text>
          <Button
            size="xl"
            onClick={startQuiz}
            rightSection={<IconArrowRight size="1.2rem" />}
            color="teal"
          >
            Start the free quiz
          </Button>
        </Box>
      </Container>

      {/* Sticky bottom CTA for mobile */}
      {showStickyButton && (
        <Box
          hiddenFrom="md"
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "0.75rem 1rem",
            background: "white",
            borderTop: "1px solid #eee",
            zIndex: 100,
          }}
        >
          <Button
            size="md"
            onClick={startQuiz}
            rightSection={<IconArrowRight size="1rem" />}
            color="teal"
            fullWidth
          >
            {t("finalCta.button")}
          </Button>
        </Box>
      )}

      {/* Spacer for sticky CTA */}
      <Box hiddenFrom="md" h={60} />

      {/* CSS for hover effects */}
      <style jsx global>{`
        .hover-lift:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        .hover-lift {
          transition: transform 0.2s, box-shadow 0.2s;
        }
      `}</style>
    </>
  );
}
